import {createFeatureSelector, createSelector} from '@ngrx/store';
import {CheckoutsState} from '@store/checkouts/checkouts.state';
import {BorrowingHistoryRecord, Checkout, CheckoutClaim, CHECKOUTS_FEATURE_KEY} from '@store/checkouts/checkouts.types';
import {FALLBACK_BORROWING_HISTORY_RECORD, FALLBACK_CHECKOUT, FALLBACK_CHECKOUT_CLAIM} from './checkouts.fallback';
import {MxSelector, MxSelectorCreator, NO_NAME_PREFIX} from '@aviary/store/store.types';
import {
  generateIndependentSelectorCreatorsFor,
  generateLoadStatusSelectorsFor,
  generateSelectorCreatorsDependentOn
} from '@aviary/store/store.selector-generators';
import {CatalogRecord} from '@store/catalog/catalog.types';
import {FALLBACK_CATALOG_RECORD} from '@store/catalog/catalog.fallback';
import {isPopulatedArray, isPopulatedString, isPositiveNumber} from '@aviary/store/common/typing.helpers';
import {FALLBACK_MATERIAL_TYPE} from '@aviary/store/common/common.fallback';
import {isOverdue} from '@aviary/store/common/date.helpers';
import {DateOnlyIsoString} from '@aviary/store/common/common.types';

export namespace CheckoutsSelectors {
  export const entireState: MxSelector<CheckoutsState> = createFeatureSelector(CHECKOUTS_FEATURE_KEY);

  export namespace StatelessCheckouts {
    export const {
      dueDateFor,
      itemTitleFor,
      itemBarcodeFor,
      catalogRecordIdFor,
      itemMediaTypeFor,
      checkoutTimeFor,
      claimIdFor,
      holdExistsFor,
      imageUrlFor,
      identifierFor
    } = generateIndependentSelectorCreatorsFor(FALLBACK_CHECKOUT, NO_NAME_PREFIX);

    export const overDueFor: MxSelectorCreator<[Checkout], boolean> =
      _checkout => createSelector(
        dueDateFor(_checkout),
        dueDate => isOverdue(dueDate)
      );

    export const hasNoHolds: MxSelectorCreator<[Checkout], boolean> =
      checkout => createSelector(
        holdExistsFor(checkout),
        (holdExists) => !holdExists
      );

    export const hasNoClaim: MxSelectorCreator<[Checkout], boolean> =
      checkout => createSelector(
        claimIdFor(checkout),
        (claimId) => !(claimId > 0)
      );

    export const buildCatalogRecordFor: MxSelectorCreator<[Checkout], CatalogRecord> =
      checkout => createSelector(
        catalogRecordIdFor(checkout),
        itemTitleFor(checkout),
        identifierFor(checkout),
        imageUrlFor(checkout),
        (id, title, identifier, imageUrl) => ({
          ...FALLBACK_CATALOG_RECORD,
          id, title, identifier, imageUrl
        })
      );

  } // namespace StatelessCheckouts

  export namespace StatelessClaims {
    export const {
      checkoutFor,
      createdFor,
      catalogRecordIdentifierFor,
      patronCommentFor,
      materialTypeFor,
      typeFor,
    } = generateIndependentSelectorCreatorsFor(FALLBACK_CHECKOUT_CLAIM, NO_NAME_PREFIX);

    export const {
      itemTitleFor,
      itemBarcodeFor,
      catalogRecordIdFor,
      itemMediaTypeFor,
      imageUrlFor,
    } = generateSelectorCreatorsDependentOn(checkoutFor, FALLBACK_CHECKOUT, NO_NAME_PREFIX);

    export const hasPatronCommentFor: MxSelectorCreator<[CheckoutClaim], boolean> =
      claim => createSelector(
        patronCommentFor(claim),
        isPopulatedString
      );

    export const buildCatalogRecordFor: MxSelectorCreator<[CheckoutClaim], CatalogRecord> =
      claim => createSelector(
        catalogRecordIdFor(claim),
        itemTitleFor(claim),
        materialTypeFor(claim),
        catalogRecordIdentifierFor(claim),
        imageUrlFor(claim),
        (id, title, materialType, identifier, imageUrl) => ({
          ...FALLBACK_CATALOG_RECORD,
          id, title, materialType, identifier, imageUrl
        })
      );
  } // namespace StatelessClaims

  export namespace CheckedOut {
    const subState: MxSelector<CheckoutsState['checkedOut']> = createSelector(
      entireState,
      state => state.checkedOut
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded,
    } = generateLoadStatusSelectorsFor(subState);

    export const checkouts: MxSelector<Checkout[]> = createSelector(
      subState,
      ({data}) => data
    );

    export const hasCheckouts: MxSelector<boolean> = createSelector(
      checkouts,
      isPopulatedArray
    );

    export const singleCheckout: MxSelector<Checkout> = createSelector(
      checkouts,
      (_checkouts) => isPopulatedArray(_checkouts) ? _checkouts[0] : FALLBACK_CHECKOUT
    );

    export const {
      dueDateFor,
      itemTitleFor,
      itemBarcodeFor,
      catalogRecordIdFor,
      itemMediaTypeFor,
      checkoutTimeFor,
      claimIdFor,
      holdExistsFor,
      imageUrlFor,
      identifierFor,
      overDueFor,
      hasNoHolds,
      hasNoClaim,
      buildCatalogRecordFor
    } = StatelessCheckouts;
  } // namespace CheckedOut

  export namespace NextCheckedOutDueDate {
    const substate: MxSelector<CheckoutsState['nextCheckedOutDueDate']> = createSelector(
      entireState,
      ({nextCheckedOutDueDate}) => nextCheckedOutDueDate
    );

    export const date: MxSelector<DateOnlyIsoString | undefined> = createSelector(
      substate,
      ({date}) => date
    );
  } // namespace NextCheckedOutDueDate

  export namespace Claims {
    const subState: MxSelector<CheckoutsState['claims']> = createSelector(
      entireState,
      state => state.claims
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded,
    } = generateLoadStatusSelectorsFor(subState);

    export const claims: MxSelector<CheckoutClaim[]> = createSelector(
      subState,
      ({data}) => data
    );

    export const numberOfClaims: MxSelector<number> = createSelector(
      claims,
      (data) => data.length
    );

    export const hasClaims: MxSelector<boolean> = createSelector(
      numberOfClaims,
      isPositiveNumber
    );

    export const {
      buildCatalogRecordFor,
      catalogRecordIdFor,
      checkoutFor,
      createdFor,
      hasPatronCommentFor,
      catalogRecordIdentifierFor,
      imageUrlFor,
      itemBarcodeFor,
      itemMediaTypeFor,
      itemTitleFor,
      materialTypeFor,
      patronCommentFor,
      typeFor,
    } = StatelessClaims;
  }

  export namespace BorrowingHistory {
    const subState: MxSelector<CheckoutsState['borrowingHistory']> = createSelector(
      entireState,
      state => state.borrowingHistory
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded,
      finishedLoading
    } = generateLoadStatusSelectorsFor(subState);

    export const borrowingHistory: MxSelector<BorrowingHistoryRecord[]> = createSelector(
      subState,
      ({data}) => data
    );

    export const catalogRecordIds: MxSelector<number[]> = createSelector(
      borrowingHistory,
      borrowingRecords => borrowingRecords.map(record => record.catalogRecordId)
    );

    export const {
      checkoutDateFor,
      titleFor,
      authorFor,
      materialTypeFor,
      checkinDateFor,
      catalogRecordIdFor,
      catalogRecordIdentifierFor,
    } = generateIndependentSelectorCreatorsFor(FALLBACK_BORROWING_HISTORY_RECORD, NO_NAME_PREFIX);

    export const {
      materialTypeDescriptionFor
    } = generateSelectorCreatorsDependentOn(materialTypeFor, FALLBACK_MATERIAL_TYPE, 'materialType');

    export const buildCatalogRecordFor: MxSelectorCreator<[BorrowingHistoryRecord], CatalogRecord> =
      borrowingRecord => createSelector(
        catalogRecordIdFor(borrowingRecord),
        titleFor(borrowingRecord),
        catalogRecordIdentifierFor(borrowingRecord),
        (id, title, identifier) => ({
          ...FALLBACK_CATALOG_RECORD,
          id, title, identifier
        })
      );
  }

  export namespace DependentCheckedOut {
    const subState: MxSelector<CheckoutsState['dependentCheckedOut']> = createSelector(
      entireState,
      state => state.dependentCheckedOut
    );

    export const {
      loading,
      awaitingFirstLoad,
      awaitingAnyLoad,
      loaded
    } = generateLoadStatusSelectorsFor(subState);

    export const checkouts: MxSelector<Checkout[]> = createSelector(
      subState,
      ({data}) => data
    );

    export const hasCheckouts: MxSelector<boolean> = createSelector(
      checkouts,
      isPopulatedArray
    );

    export const singleCheckout: MxSelector<Checkout> = createSelector(
      checkouts,
      (_checkouts) => isPopulatedArray(_checkouts) ? _checkouts[0] : FALLBACK_CHECKOUT
    );

    export const {
      dueDateFor,
      itemTitleFor,
      itemBarcodeFor,
      catalogRecordIdFor,
      itemMediaTypeFor,
      checkoutTimeFor,
      claimIdFor,
      holdExistsFor,
      imageUrlFor,
      identifierFor,
      overDueFor,
      hasNoHolds,
      hasNoClaim,
      buildCatalogRecordFor
    } = StatelessCheckouts;
  } // namespace DependentCheckedOut
}
